import { CommonModule } from '@angular/common';
import { scrollToFirstError } from '@anihis/shared/src/functions/scrollToFirstError';
import {
  ChangeDetectionStrategy,
  Component,
  effect,
  inject,
  signal,
} from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import {
  FormsModule,
  ReactiveFormsModule,
  Validators,
  ValidatorFn,
  AbstractControl,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatError } from '@angular/material/form-field';
import { TranslocoModule } from '@jsverse/transloco';
import { FormBaseComponent } from '@anihis/shared/src/shared/base-components/form-base.component';
import { RegexPatterns } from '@anihis/shared/src/constants/regex-patterns';
import { ClaimAccountStore } from './claim-account.store';
import { LoadingStatus } from '../../shared/enums/loading';

// Custom URL schemes registered in AndroidManifest / Info.plist
const CLIENT_APP_SCHEME = 'com.anihis.app';
const VET_APP_SCHEME = 'com.anihis.veterinarian.app';

@Component({
  selector: 'anihis-claim-account',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    TranslocoModule,
    MatButtonModule,
    MatError,
    RouterModule,
  ],
  templateUrl: './claim-account.component.html',
  styleUrl: './claim-account.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ClaimAccountStore],
})
export class ClaimAccountComponent extends FormBaseComponent {
  store = inject(ClaimAccountStore);
  private route = inject(ActivatedRoute);
  private router = inject(Router);

  readonly status = LoadingStatus;
  showValidationErrors = signal(false);
  showPassword = signal(false);
  showPassword2 = signal(false);

  // After successful claim — set to redirect or open app
  successDeepLinkUrl = signal<string>('');
  successAppName = signal<string>('');
  successRedirectToLogin = signal(false);

  userId = signal<string>('');
  token = signal<string>('');
  role = signal<string>('');

  constructor() {
    super();
    this.initializeForm();

    this.route.queryParams.subscribe((params) => {
      const userId = params['userId'] || '';
      const token = params['token'] || '';
      const role = params['role'] || '';

      this.userId.set(userId);
      this.token.set(token);
      this.role.set(role);

      this.store.validateClaimLink(userId, token).subscribe();
    });

    // Redirect portal roles to login after success
    effect(() => {
      if (this.successRedirectToLogin()) {
        this.router.navigateByUrl('/login');
      }
    });
  }

  private passwordsMatchValidator: ValidatorFn = (control: AbstractControl) => {
    const pass = control.get('passwordNew')?.value;
    const confirm = control.get('passwordNew2')?.value;
    if (!pass || !confirm) return null;
    return pass === confirm ? null : { passwordsDontMatch: true };
  };

  private initializeForm() {
    this.form = this.fb.group(
      {
        passwordNew: [
          '',
          [
            Validators.required,
            Validators.pattern(RegexPatterns.PASSWORD_STRENGTH_REGEX),
          ],
        ],
        passwordNew2: ['', [Validators.required]],
      },
      { validators: this.passwordsMatchValidator },
    );
  }

  save() {
    this.showValidationErrors.set(true);

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      setTimeout(() => scrollToFirstError(), 50);
      return;
    }

    const newPassword = this.form.get('passwordNew')?.value;
    const role = this.role();
    this.store
      .claimAccount(this.userId(), this.token(), newPassword, role)
      .subscribe({
        next: (succeeded) => {
          if (!succeeded) return;
          this.form.reset();
          this.showValidationErrors.set(false);

          if (role === 'Owner') {
            this.successDeepLinkUrl.set(`${CLIENT_APP_SCHEME}://login`);
            this.successAppName.set('Anihis');
          } else if (role === 'Veterinarian') {
            this.successDeepLinkUrl.set(`${VET_APP_SCHEME}://login`);
            this.successAppName.set('Anihis Vet');
          } else {
            this.successRedirectToLogin.set(true);
          }
        },
      });
  }
}
